import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { Layout } from '@/components/layout';
import { useAuthStore, useAuthHeaders } from '@/lib/auth-store';
import { useGetMe } from '@workspace/api-client-react';
import { useToast } from '@/hooks/use-toast';
import {
  Building2, Plus, Trash2, Pencil, Loader2, X, Check,
  Mail, Phone, MapPin, Tag, Store, ChevronDown, ChevronUp,
  ToggleLeft, ToggleRight, AlertTriangle, Settings, Save,
  Upload, ImageOff, Package, Clock, RefreshCw, DollarSign,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

/**
 * Convert a stored logoUrl value to a browser-displayable URL.
 * Object paths (/objects/…) are served via the authenticated proxy route.
 * External URLs and legacy signed GCS URLs are returned unchanged.
 */
function logoDisplayUrl(logoUrl: string): string {
  if (logoUrl.startsWith('/objects/')) {
    return `${BASE}/api/po/logo-file?path=${encodeURIComponent(logoUrl)}`;
  }
  return logoUrl;
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface ReorderRule {
  id: string;
  storeId: string;
  poSupplierId: string;
  name: string;
  triggerType: 'sku' | 'tag';
  triggerValue: string;
  thresholdQty: number;
  reorderQty: number;
  unitPriceCents: number;
  enabled: boolean;
  lastFiredAt: string | null;
  lastCheckedAt: string | null;
  lastStockQty: number | null;
  createdAt: string;
  updatedAt: string;
  // joined from supplier
  supplierName: string | null;
  supplierAutoSend: boolean | null;
  supplierEmail: string | null;
}

interface ShopifyStore {
  id: string;
  nickname: string;
  storeUrl: string;
}

interface SupplierAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

interface PoSupplier {
  id: string;
  storeId: string;
  name: string;
  email: string;
  phone: string | null;
  notes: string | null;
  supplierAddress: SupplierAddress | null;
  vendorNames: string[] | null;
  productTags: string[] | null;
  autoSend: boolean;
  dropshipMode: boolean;
  createdAt: string;
  updatedAt: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function linkingRuleSummary(supplier: PoSupplier): string {
  const parts: string[] = [];
  if (supplier.vendorNames && supplier.vendorNames.length > 0) {
    parts.push(`${supplier.vendorNames.length} vendor${supplier.vendorNames.length !== 1 ? 's' : ''}`);
  }
  if (supplier.productTags && supplier.productTags.length > 0) {
    parts.push(`${supplier.productTags.length} tag${supplier.productTags.length !== 1 ? 's' : ''}`);
  }
  if (parts.length === 0) return 'No linking rule set';
  return parts.join(' + ');
}

// ── Chip input component ──────────────────────────────────────────────────────

function ChipInput({
  label,
  values,
  onChange,
  placeholder,
}: {
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  const [input, setInput] = useState('');

  function addChip() {
    const val = input.trim();
    if (val && !values.includes(val)) {
      onChange([...values, val]);
    }
    setInput('');
  }

  function removeChip(v: string) {
    onChange(values.filter((x) => x !== v));
  }

  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">{label}</label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {values.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/15 text-primary text-xs font-medium"
          >
            {v}
            <button type="button" onClick={() => removeChip(v)} className="hover:text-destructive">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          className="flex-1 bg-muted/40 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              addChip();
            }
          }}
          placeholder={placeholder ?? 'Type and press Enter'}
        />
        <button
          type="button"
          onClick={addChip}
          className="px-3 py-1.5 rounded-lg bg-muted/60 border border-border text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Add
        </button>
      </div>
    </div>
  );
}

// ── Vendor multi-select ───────────────────────────────────────────────────────

function VendorSelect({
  storeId,
  selected,
  onChange,
  headers,
}: {
  storeId: string;
  selected: string[];
  onChange: (v: string[]) => void;
  headers: Record<string, string>;
}) {
  const [vendors, setVendors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!storeId) return;
    setLoading(true);
    fetch(`${BASE}/api/stores/${storeId}/vendors`, { headers })
      .then((r) => r.json())
      .then((data) => setVendors(Array.isArray(data) ? data : []))
      .catch(() => setVendors([]))
      .finally(() => setLoading(false));
  }, [storeId]);

  // Close on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function toggle(v: string) {
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else onChange([...selected, v]);
  }

  return (
    <div ref={ref} className="relative">
      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Vendor names</label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-left focus:outline-none focus:ring-1 focus:ring-primary/40"
      >
        <span className={selected.length === 0 ? 'text-muted-foreground/50' : 'text-foreground'}>
          {loading ? 'Loading vendors…' : selected.length === 0 ? 'Select vendors' : selected.join(', ')}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {open && vendors.length > 0 && (
        <div className="absolute z-50 top-full mt-1 w-full bg-background border border-border rounded-xl shadow-lg max-h-52 overflow-y-auto">
          {vendors.map((v) => (
            <button
              type="button"
              key={v}
              onClick={() => toggle(v)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted/50 transition-colors"
            >
              <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${selected.includes(v) ? 'bg-primary border-primary' : 'border-border'}`}>
                {selected.includes(v) && <Check className="w-2.5 h-2.5 text-primary-foreground" />}
              </div>
              <span className={selected.includes(v) ? 'text-foreground font-medium' : 'text-muted-foreground'}>{v}</span>
            </button>
          ))}
        </div>
      )}
      {open && !loading && vendors.length === 0 && (
        <div className="absolute z-50 top-full mt-1 w-full bg-background border border-border rounded-xl shadow-lg px-3 py-3 text-xs text-muted-foreground">
          No vendors found for this store.
        </div>
      )}
    </div>
  );
}

// ── Address block ─────────────────────────────────────────────────────────────

function AddressBlock({
  value,
  onChange,
}: {
  value: SupplierAddress;
  onChange: (v: SupplierAddress) => void;
}) {
  function set(key: keyof SupplierAddress, val: string) {
    onChange({ ...value, [key]: val || undefined });
  }

  return (
    <div className="space-y-2">
      <input
        className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40"
        placeholder="Address line 1"
        value={value.line1 ?? ''}
        onChange={(e) => set('line1', e.target.value)}
      />
      <input
        className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40"
        placeholder="Address line 2"
        value={value.line2 ?? ''}
        onChange={(e) => set('line2', e.target.value)}
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40"
          placeholder="City"
          value={value.city ?? ''}
          onChange={(e) => set('city', e.target.value)}
        />
        <input
          className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40"
          placeholder="State / Province"
          value={value.state ?? ''}
          onChange={(e) => set('state', e.target.value)}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input
          className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40"
          placeholder="Postcode"
          value={value.postcode ?? ''}
          onChange={(e) => set('postcode', e.target.value)}
        />
        <input
          className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40"
          placeholder="Country"
          value={value.country ?? ''}
          onChange={(e) => set('country', e.target.value)}
        />
      </div>
    </div>
  );
}

// ── Toggle row ────────────────────────────────────────────────────────────────

function ToggleRow({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <button type="button" onClick={() => onChange(!value)} className="flex-shrink-0 mt-0.5">
        {value
          ? <ToggleRight className="w-7 h-7 text-primary" />
          : <ToggleLeft className="w-7 h-7 text-muted-foreground/40" />}
      </button>
    </div>
  );
}

// ── Blank form state ──────────────────────────────────────────────────────────

function blankForm() {
  return {
    name: '',
    email: '',
    phone: '',
    notes: '',
    address: {} as SupplierAddress,
    linkingMode: 'vendor' as 'vendor' | 'tag' | 'both',
    vendorNames: [] as string[],
    productTags: [] as string[],
    autoSend: false,
    dropshipMode: false,
  };
}

// ── Main Component ─────────────────────────────────────────────────────────────

export function PoSuppliers() {
  const token = useAuthStore((s) => s.token);
  const authOpts = useAuthHeaders();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const { data: me, isLoading: meLoading } = useGetMe({
    request: authOpts,
    query: { enabled: !!token, retry: false },
  });

  // Feature flag redirect
  useEffect(() => {
    if (!meLoading && me && !(me as any).featureFlags?.purchase_orders) {
      setLocation('/');
    }
  }, [meLoading, me]);

  const [stores, setStores] = useState<ShopifyStore[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [suppliers, setSuppliers] = useState<PoSupplier[]>([]);
  const [loading, setLoading] = useState(false);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);
  const [showAddress, setShowAddress] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<PoSupplier | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Tab
  const [activeTab, setActiveTab] = useState<'suppliers' | 'reorder-rules'>('suppliers');

  // PO Settings
  const [poSettings, setPoSettings] = useState({ logoUrl: '', fromName: '', headerMessage: '', footerMessage: '' });
  const [poSettingsLoading, setPoSettingsLoading] = useState(false);
  const [poSettingsSaving, setPoSettingsSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoImgError, setLogoImgError] = useState(false);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  // Reorder Rules state
  const [rules, setRules] = useState<ReorderRule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [ruleDrawerOpen, setRuleDrawerOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [ruleSaving, setRuleSaving] = useState(false);
  const [deleteRuleTarget, setDeleteRuleTarget] = useState<ReorderRule | null>(null);
  const [deleteRuleLoading, setDeleteRuleLoading] = useState(false);
  const blankRuleForm = () => ({
    name: '',
    poSupplierId: '',
    triggerType: 'sku' as 'sku' | 'tag',
    triggerValue: '',
    thresholdQty: 5,
    reorderQty: 10,
    unitPriceCents: 0,
    enabled: true,
  });
  const [ruleForm, setRuleForm] = useState(blankRuleForm());

  // ── Load stores ─────────────────────────────────────────────────────────────

  useEffect(() => {
    fetch(`${BASE}/api/stores`, { headers })
      .then((r) => r.json())
      .then((data: ShopifyStore[]) => {
        const list = Array.isArray(data) ? data : [];
        setStores(list);
        if (list.length > 0 && !selectedStoreId) {
          setSelectedStoreId(list[0]!.id);
        }
      })
      .catch(() => {});
  }, []);

  // ── Load suppliers + settings when store changes ─────────────────────────────

  useEffect(() => {
    if (!selectedStoreId) return;
    loadSuppliers();
    loadPoSettings();
    loadRules();
  }, [selectedStoreId]);

  // ── Reorder Rules functions ──────────────────────────────────────────────────

  async function loadRules() {
    if (!selectedStoreId) return;
    setRulesLoading(true);
    try {
      const res = await fetch(`${BASE}/api/po/reorder-rules?storeId=${selectedStoreId}`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed to load reorder rules');
      setRules(data.rules ?? []);
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setRulesLoading(false);
    }
  }

  function openAddRule() {
    setEditingRuleId(null);
    setRuleForm(blankRuleForm());
    setRuleDrawerOpen(true);
  }

  function openEditRule(r: ReorderRule) {
    setEditingRuleId(r.id);
    setRuleForm({
      name: r.name,
      poSupplierId: r.poSupplierId,
      triggerType: r.triggerType,
      triggerValue: r.triggerValue,
      thresholdQty: r.thresholdQty,
      reorderQty: r.reorderQty,
      unitPriceCents: r.unitPriceCents,
      enabled: r.enabled,
    });
    setRuleDrawerOpen(true);
  }

  async function handleSaveRule() {
    if (!ruleForm.name.trim() || !ruleForm.poSupplierId || !ruleForm.triggerValue.trim()) {
      toast({ title: 'Name, supplier, and trigger value are required', variant: 'destructive' });
      return;
    }
    setRuleSaving(true);
    try {
      const url = editingRuleId
        ? `${BASE}/api/po/reorder-rules/${editingRuleId}`
        : `${BASE}/api/po/reorder-rules`;
      const res = await fetch(url, {
        method: editingRuleId ? 'PUT' : 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeId: selectedStoreId, ...ruleForm }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Save failed');
      toast({ title: editingRuleId ? 'Rule updated' : 'Reorder rule created' });
      setRuleDrawerOpen(false);
      await loadRules();
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setRuleSaving(false);
    }
  }

  async function handleDeleteRule() {
    if (!deleteRuleTarget) return;
    setDeleteRuleLoading(true);
    try {
      const res = await fetch(`${BASE}/api/po/reorder-rules/${deleteRuleTarget.id}`, {
        method: 'DELETE',
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Delete failed');
      toast({ title: `Rule "${deleteRuleTarget.name}" deleted` });
      setDeleteRuleTarget(null);
      await loadRules();
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setDeleteRuleLoading(false);
    }
  }

  async function handleToggleRule(r: ReorderRule) {
    try {
      const res = await fetch(`${BASE}/api/po/reorder-rules/${r.id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !r.enabled }),
      });
      if (!res.ok) throw new Error('Toggle failed');
      await loadRules();
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    }
  }

  function fmtDate(dateStr: string | null, placeholder = 'Never'): string {
    if (!dateStr) return placeholder;
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  function fmtCents(cents: number): string {
    if (cents === 0) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  }

  async function loadPoSettings() {
    if (!selectedStoreId) return;
    setPoSettingsLoading(true);
    try {
      const res = await fetch(`${BASE}/api/po/settings?storeId=${selectedStoreId}`, { headers });
      const data = await res.json();
      if (res.ok && data.settings) {
        setPoSettings({
          logoUrl: data.settings.logoUrl ?? '',
          fromName: data.settings.fromName ?? '',
          headerMessage: data.settings.headerMessage ?? '',
          footerMessage: data.settings.footerMessage ?? '',
        });
      }
    } catch {
      // non-fatal
    } finally {
      setPoSettingsLoading(false);
    }
  }

  async function handleLogoUpload(file: File) {
    setLogoUploading(true);
    try {
      // Step 1: request a presigned GCS upload URL from our API
      const urlRes = await fetch(`${BASE}/api/po/logo-upload`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId: selectedStoreId,
          name: file.name,
          size: file.size,
          contentType: file.type,
        }),
      });
      const urlData = await urlRes.json();
      if (!urlRes.ok) throw new Error(urlData.error ?? 'Failed to get upload URL');

      const { uploadUrl, objectPath } = urlData as { uploadUrl: string; objectPath: string };

      // Step 2: PUT file bytes directly to GCS (presigned URL, no auth header)
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!putRes.ok) throw new Error('Upload to storage failed');

      // Step 3: store the object path — the server re-signs it fresh each time
      // a PO email is sent, so it never expires.
      setPoSettings((s) => ({ ...s, logoUrl: objectPath }));
      setLogoImgError(false);
      toast({ title: 'Logo uploaded — click Save Settings to apply.' });
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setLogoUploading(false);
      // Reset file input so the same file can be re-selected after an error
      if (logoFileInputRef.current) logoFileInputRef.current.value = '';
    }
  }

  async function savePoSettings() {
    setPoSettingsSaving(true);
    try {
      const res = await fetch(`${BASE}/api/po/settings`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId: selectedStoreId,
          logoUrl: poSettings.logoUrl.trim() || null,
          fromName: poSettings.fromName.trim() || null,
          headerMessage: poSettings.headerMessage.trim() || null,
          footerMessage: poSettings.footerMessage.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed to save');
      toast({ title: 'PO email settings saved' });
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setPoSettingsSaving(false);
    }
  }

  async function loadSuppliers() {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/po/suppliers?storeId=${selectedStoreId}`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed to load suppliers');
      setSuppliers(data.suppliers ?? []);
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }

  // ── Open drawer ──────────────────────────────────────────────────────────────

  function openAdd() {
    setEditingId(null);
    setForm(blankForm());
    setShowAddress(false);
    setDrawerOpen(true);
  }

  function openEdit(s: PoSupplier) {
    setEditingId(s.id);
    // Determine linking mode from data
    const hasVendors = (s.vendorNames?.length ?? 0) > 0;
    const hasTags = (s.productTags?.length ?? 0) > 0;
    const linkingMode: 'vendor' | 'tag' | 'both' =
      hasVendors && hasTags ? 'both' : hasTags ? 'tag' : 'vendor';

    setForm({
      name: s.name,
      email: s.email,
      phone: s.phone ?? '',
      notes: s.notes ?? '',
      address: s.supplierAddress ?? {},
      linkingMode,
      vendorNames: s.vendorNames ?? [],
      productTags: s.productTags ?? [],
      autoSend: s.autoSend,
      dropshipMode: s.dropshipMode,
    });
    setShowAddress(!!(s.supplierAddress && Object.values(s.supplierAddress).some(Boolean)));
    setDrawerOpen(true);
  }

  // ── Save ─────────────────────────────────────────────────────────────────────

  async function handleSave() {
    if (!form.name.trim() || !form.email.trim()) {
      toast({ title: 'Name and email are required', variant: 'destructive' });
      return;
    }

    // Build linking arrays based on mode
    const vendorNames = form.linkingMode === 'tag' ? [] : form.vendorNames;
    const productTags = form.linkingMode === 'vendor' ? [] : form.productTags;

    const body = {
      storeId: selectedStoreId,
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || null,
      notes: form.notes.trim() || null,
      supplierAddress: showAddress && Object.values(form.address).some(Boolean) ? form.address : null,
      vendorNames: vendorNames.length > 0 ? vendorNames : null,
      productTags: productTags.length > 0 ? productTags : null,
      autoSend: form.autoSend,
      dropshipMode: form.dropshipMode,
    };

    setSaving(true);
    try {
      const url = editingId
        ? `${BASE}/api/po/suppliers/${editingId}`
        : `${BASE}/api/po/suppliers`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Save failed');
      toast({ title: editingId ? 'Supplier updated' : `${form.name} added` });
      setDrawerOpen(false);
      await loadSuppliers();
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  // ── Delete ───────────────────────────────────────────────────────────────────

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const res = await fetch(`${BASE}/api/po/suppliers/${deleteTarget.id}`, {
        method: 'DELETE',
        headers,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Delete failed');
      toast({ title: `${deleteTarget.name} removed` });
      setDeleteTarget(null);
      await loadSuppliers();
    } catch (e: any) {
      toast({ title: e.message, variant: 'destructive' });
    } finally {
      setDeleteLoading(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
              <Building2 className="w-6 h-6 text-primary" />
              Purchase Orders
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage suppliers, settings, and automated reorder rules.
            </p>
          </div>
          {activeTab === 'suppliers' ? (
            <button
              onClick={openAdd}
              disabled={!selectedStoreId}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              Add Supplier
            </button>
          ) : (
            <button
              onClick={openAddRule}
              disabled={!selectedStoreId || suppliers.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={suppliers.length === 0 ? 'Add a supplier first' : undefined}
            >
              <Plus className="w-4 h-4" />
              Add Rule
            </button>
          )}
        </div>

        {/* Store picker */}
        {stores.length > 1 && (
          <div className="flex items-center gap-3">
            <Store className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <select
              value={selectedStoreId}
              onChange={(e) => setSelectedStoreId(e.target.value)}
              className="bg-muted/40 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
            >
              {stores.map((s) => (
                <option key={s.id} value={s.id}>{s.nickname || s.storeUrl}</option>
              ))}
            </select>
          </div>
        )}

        {/* No stores */}
        {stores.length === 0 && (
          <div className="bg-muted/30 border border-border rounded-xl p-6 text-center text-muted-foreground text-sm">
            No stores connected.{' '}
            <a href={`${BASE}/stores`} className="text-primary hover:underline">Connect a store</a> first.
          </div>
        )}

        {/* Tab navigation */}
        {selectedStoreId && (
          <div className="flex gap-1 border-b border-border">
            <button
              onClick={() => setActiveTab('suppliers')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'suppliers'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" />
                Suppliers
              </span>
            </button>
            <button
              onClick={() => setActiveTab('reorder-rules')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'reorder-rules'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <Package className="w-3.5 h-3.5" />
                Reorder Rules
                {rules.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-semibold">
                    {rules.length}
                  </span>
                )}
              </span>
            </button>
          </div>
        )}

        {/* ── Reorder Rules tab ─────────────────────────────────────────────────── */}
        {activeTab === 'reorder-rules' && selectedStoreId && (
          <>
            {rulesLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : rules.length === 0 ? (
              <div className="bg-muted/30 border border-border rounded-xl p-10 text-center">
                <Package className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-30" />
                <p className="text-sm font-medium text-muted-foreground">No reorder rules yet</p>
                <p className="text-xs text-muted-foreground/60 mt-1 max-w-sm mx-auto">
                  Reorder rules automatically create draft POs when stock drops to or below a threshold. The scheduler checks daily.
                </p>
                {suppliers.length === 0 ? (
                  <p className="mt-4 text-xs text-amber-400">Add at least one supplier before creating a reorder rule.</p>
                ) : (
                  <button
                    onClick={openAddRule}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Reorder Rule
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {rules.map((r) => (
                  <div key={r.id} className="bg-muted/30 border border-border rounded-xl px-5 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm text-foreground">{r.name}</span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            r.enabled
                              ? 'bg-emerald-500/15 text-emerald-400'
                              : 'bg-muted/50 text-muted-foreground'
                          }`}>
                            {r.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-400 text-[10px] font-medium">
                            {r.triggerType === 'sku' ? 'SKU' : 'Tag'}: {r.triggerValue}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
                          <span>Reorder when ≤ <strong className="text-foreground">{r.thresholdQty}</strong> units</span>
                          <span>Order <strong className="text-foreground">{r.reorderQty}</strong> units</span>
                          {r.unitPriceCents > 0 && (
                            <span>Unit price: <strong className="text-foreground">{fmtCents(r.unitPriceCents)}</strong></span>
                          )}
                          <span>Supplier: <strong className="text-foreground">{r.supplierName ?? 'Unknown'}</strong></span>
                          {r.supplierAutoSend && (
                            <span className="text-amber-400">Auto-send on</span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground/60">
                          <span className="flex items-center gap-1">
                            <RefreshCw className="w-3 h-3" />
                            Checked: {fmtDate(r.lastCheckedAt, 'Never checked')}
                            {r.lastStockQty !== null && ` (${r.lastStockQty} in stock)`}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Last fired: {fmtDate(r.lastFiredAt, 'Never fired')}
                          </span>
                        </div>
                        {r.supplierAutoSend === false && r.supplierEmail && (
                          <p className="text-[10px] text-muted-foreground/50">
                            Auto-send is off for this supplier — rules will create drafts only.
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleToggleRule(r)}
                          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                          title={r.enabled ? 'Disable rule' : 'Enable rule'}
                        >
                          {r.enabled
                            ? <ToggleRight className="w-5 h-5 text-primary" />
                            : <ToggleLeft className="w-5 h-5" />}
                        </button>
                        <button
                          onClick={() => openEditRule(r)}
                          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteRuleTarget(r)}
                          className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Suppliers tab ──────────────────────────────────────────────────────── */}
        {activeTab === 'suppliers' && <>
        {/* Supplier list */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : suppliers.length === 0 && selectedStoreId ? (
          <div className="bg-muted/30 border border-border rounded-xl p-10 text-center">
            <Building2 className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-sm font-medium text-muted-foreground">No suppliers yet</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Add your first supplier to start generating purchase orders.</p>
            <button
              onClick={openAdd}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Supplier
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {suppliers.map((s) => (
              <div key={s.id} className="bg-muted/30 border border-border rounded-xl px-5 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="font-medium text-sm text-foreground">{s.name}</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />{s.email}
                      </span>
                      {s.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />{s.phone}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-1.5">
                      {/* Linking rule chips */}
                      {(s.vendorNames ?? []).map((v) => (
                        <span key={v} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-[11px] font-medium">
                          <Store className="w-2.5 h-2.5" />{v}
                        </span>
                      ))}
                      {(s.productTags ?? []).map((t) => (
                        <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-400 text-[11px] font-medium">
                          <Tag className="w-2.5 h-2.5" />{t}
                        </span>
                      ))}
                      {(!s.vendorNames?.length && !s.productTags?.length) && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/50 text-muted-foreground text-[11px]">
                          No linking rule
                        </span>
                      )}
                    </div>
                    <div className="flex gap-3 text-[11px] text-muted-foreground/60 mt-1">
                      {s.autoSend && <span className="text-amber-400">Auto-send on</span>}
                      {s.dropshipMode && <span className="text-sky-400">Dropship mode</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => openEdit(s)}
                      className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(s)}
                      className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* PO Email Settings */}
        {selectedStoreId && (
          <div className="bg-muted/30 border border-border rounded-xl overflow-hidden">

            <button
              type="button"
              onClick={() => document.getElementById('po-settings-body')?.classList.toggle('hidden')}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-semibold text-foreground">PO Email Settings</span>
              </div>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </button>
            <div id="po-settings-body" className="hidden border-t border-border px-5 py-4 space-y-4">
              <p className="text-xs text-muted-foreground">
                Customise the appearance of purchase order emails sent to your suppliers.
              </p>
              {poSettingsLoading ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading settings…
                </div>
              ) : (
                <>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                      From Name
                    </label>
                    <input
                      className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40"
                      placeholder="e.g. Acme Tyres (shown in email subject & header)"
                      value={poSettings.fromName}
                      onChange={(e) => setPoSettings({ ...poSettings, fromName: e.target.value })}
                    />
                  </div>
                  {/* ── Logo upload ───────────────────────────────────── */}
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                      Logo
                    </label>
                    <div className="flex items-start gap-3">
                      {/* Preview thumbnail */}
                      <div className="flex-shrink-0 w-16 h-16 rounded-lg border border-border bg-muted/30 overflow-hidden flex items-center justify-center">
                        {poSettings.logoUrl && !logoImgError ? (
                          <img
                            src={logoDisplayUrl(poSettings.logoUrl)}
                            alt="Logo preview"
                            className="w-full h-full object-contain p-1"
                            onError={() => setLogoImgError(true)}
                          />
                        ) : (
                          <ImageOff className="w-6 h-6 text-muted-foreground/30" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 space-y-2">
                        {/* Upload button */}
                        <input
                          ref={logoFileInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleLogoUpload(file);
                          }}
                        />
                        <button
                          type="button"
                          disabled={logoUploading}
                          onClick={() => logoFileInputRef.current?.click()}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs bg-muted/60 border border-border text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors"
                        >
                          {logoUploading
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <Upload className="w-3.5 h-3.5" />}
                          {logoUploading ? 'Uploading…' : 'Upload logo file'}
                        </button>
                        {/* Fallback URL input for existing / external logos */}
                        <input
                          className="w-full bg-muted/40 border border-border rounded-lg px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40"
                          placeholder="or paste a URL: https://yourstore.com/logo.png"
                          value={poSettings.logoUrl}
                          onChange={(e) => {
                            setLogoImgError(false);
                            setPoSettings({ ...poSettings, logoUrl: e.target.value });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                      Header Message
                    </label>
                    <textarea
                      rows={2}
                      className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40 resize-none"
                      placeholder="Optional message shown below the PO header, e.g. payment terms or handling instructions."
                      value={poSettings.headerMessage}
                      onChange={(e) => setPoSettings({ ...poSettings, headerMessage: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                      Footer Message
                    </label>
                    <textarea
                      rows={2}
                      className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40 resize-none"
                      placeholder="Optional closing message, e.g. contact details or return policy."
                      value={poSettings.footerMessage}
                      onChange={(e) => setPoSettings({ ...poSettings, footerMessage: e.target.value })}
                    />
                  </div>
                  <div className="flex justify-end pt-1">
                    <button
                      onClick={savePoSettings}
                      disabled={poSettingsSaving}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
                    >
                      {poSettingsSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save Settings
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        </> /* end suppliers tab */}
      </div>

      {/* ── Add / Edit Rule Drawer ────────────────────────────────────────────── */}
      <Sheet open={ruleDrawerOpen} onOpenChange={setRuleDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle>{editingRuleId ? 'Edit Reorder Rule' : 'Add Reorder Rule'}</SheetTitle>
            <SheetDescription>
              The scheduler checks this rule once per day and creates a draft PO when stock falls at or below the threshold.
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-5">
            {/* Name */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Rule Name *</label>
              <input
                className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40"
                placeholder="e.g. Reorder ARB Snorkel"
                value={ruleForm.name}
                onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
              />
            </div>

            {/* Supplier */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Supplier *</label>
              <select
                className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
                value={ruleForm.poSupplierId}
                onChange={(e) => setRuleForm({ ...ruleForm, poSupplierId: e.target.value })}
              >
                <option value="">Select a supplier…</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            {/* Trigger type */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Trigger type</label>
              <div className="flex gap-2">
                {(['sku', 'tag'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setRuleForm({ ...ruleForm, triggerType: t })}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      ruleForm.triggerType === t
                        ? 'bg-primary/15 border-primary/40 text-primary'
                        : 'bg-muted/30 border-border text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t === 'sku' ? 'By SKU' : 'By Product Tag'}
                  </button>
                ))}
              </div>
            </div>

            {/* Trigger value */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                {ruleForm.triggerType === 'sku' ? 'SKU *' : 'Product Tag *'}
              </label>
              <input
                className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40"
                placeholder={ruleForm.triggerType === 'sku' ? 'e.g. ARB-3450010' : 'e.g. arb-snorkel'}
                value={ruleForm.triggerValue}
                onChange={(e) => setRuleForm({ ...ruleForm, triggerValue: e.target.value })}
              />
              <p className="text-[10px] text-muted-foreground/60 mt-1">
                {ruleForm.triggerType === 'sku'
                  ? 'Total inventory across all variants with this SKU will be checked.'
                  : 'Total inventory across all products with this tag will be checked.'}
              </p>
            </div>

            {/* Threshold + Reorder Qty */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Stock threshold</label>
                <input
                  type="number"
                  min={0}
                  className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
                  value={ruleForm.thresholdQty}
                  onChange={(e) => setRuleForm({ ...ruleForm, thresholdQty: Math.max(0, parseInt(e.target.value) || 0) })}
                />
                <p className="text-[10px] text-muted-foreground/60 mt-1">Fire when on-hand ≤ this</p>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Reorder qty</label>
                <input
                  type="number"
                  min={1}
                  className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
                  value={ruleForm.reorderQty}
                  onChange={(e) => setRuleForm({ ...ruleForm, reorderQty: Math.max(1, parseInt(e.target.value) || 1) })}
                />
                <p className="text-[10px] text-muted-foreground/60 mt-1">Units to order</p>
              </div>
            </div>

            {/* Unit price */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Unit price (optional)</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  className="w-full bg-muted/40 border border-border rounded-lg pl-8 pr-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
                  placeholder="0.00"
                  value={ruleForm.unitPriceCents / 100 || ''}
                  onChange={(e) => setRuleForm({ ...ruleForm, unitPriceCents: Math.max(0, Math.round(parseFloat(e.target.value || '0') * 100)) })}
                />
              </div>
            </div>

            {/* Enabled toggle */}
            <ToggleRow
              label="Enabled"
              description="When off, this rule will not fire during the daily check."
              value={ruleForm.enabled}
              onChange={(enabled) => setRuleForm({ ...ruleForm, enabled })}
            />

            {/* Save button */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleSaveRule}
                disabled={ruleSaving}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {ruleSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {ruleSaving ? 'Saving…' : editingRuleId ? 'Save Changes' : 'Add Rule'}
              </button>
              <button
                type="button"
                onClick={() => setRuleDrawerOpen(false)}
                className="px-4 py-2.5 rounded-xl border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Delete Rule Confirmation ───────────────────────────────────────────── */}
      <AlertDialog open={!!deleteRuleTarget} onOpenChange={(open) => { if (!open) setDeleteRuleTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete rule "{deleteRuleTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This reorder rule will be permanently deleted. Existing purchase orders it created will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRule}
              disabled={deleteRuleLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteRuleLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete Rule'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Add / Edit Drawer ─────────────────────────────────────────────────── */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle>{editingId ? 'Edit Supplier' : 'Add Supplier'}</SheetTitle>
            <SheetDescription>
              {editingId
                ? 'Update supplier details and linking rules.'
                : 'Add a company you order stock from. Linking rules determine which products belong to this supplier.'}
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-5">
            {/* Name */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Name *</label>
              <input
                className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40"
                placeholder="e.g. Offroad Animal"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            {/* Email */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Email *</label>
              <input
                type="email"
                className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40"
                placeholder="orders@supplier.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>

            {/* Phone */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Phone</label>
              <input
                className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40"
                placeholder="+61 2 1234 5678"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Notes</label>
              <textarea
                className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40 resize-none"
                rows={2}
                placeholder="Any notes about ordering from this supplier…"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            {/* Supplier address */}
            <div>
              <button
                type="button"
                onClick={() => setShowAddress((v) => !v)}
                className="flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors mb-2"
              >
                <MapPin className="w-3.5 h-3.5" />
                Supplier Address (Ship-from on POs)
                {showAddress ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              {showAddress && (
                <AddressBlock
                  value={form.address}
                  onChange={(address) => setForm({ ...form, address })}
                />
              )}
            </div>

            <div className="border-t border-border pt-4">
              {/* Linking rule mode */}
              <p className="text-xs font-semibold text-foreground mb-3">Linking Rule</p>
              <p className="text-xs text-muted-foreground mb-3">
                Match products to this supplier by vendor name, tag, or both.
              </p>
              <div className="flex gap-2 mb-4">
                {(['vendor', 'tag', 'both'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setForm({ ...form, linkingMode: mode })}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      form.linkingMode === mode
                        ? 'bg-primary/15 border-primary/40 text-primary'
                        : 'bg-muted/30 border-border text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {mode === 'vendor' ? 'By Vendor' : mode === 'tag' ? 'By Tag' : 'Both'}
                  </button>
                ))}
              </div>

              {/* Vendor multi-select */}
              {(form.linkingMode === 'vendor' || form.linkingMode === 'both') && (
                <div className="mb-4">
                  <VendorSelect
                    storeId={selectedStoreId}
                    selected={form.vendorNames}
                    onChange={(vendorNames) => setForm({ ...form, vendorNames })}
                    headers={headers}
                  />
                </div>
              )}

              {/* Tag chip input */}
              {(form.linkingMode === 'tag' || form.linkingMode === 'both') && (
                <div className="mb-4">
                  <ChipInput
                    label="Product tags"
                    values={form.productTags}
                    onChange={(productTags) => setForm({ ...form, productTags })}
                    placeholder="e.g. dropship-supplier-a"
                  />
                </div>
              )}
            </div>

            <div className="border-t border-border pt-4 space-y-4">
              <ToggleRow
                label="Auto-send POs"
                description="When on, POs for this supplier are sent automatically when created. When off, they are saved as Draft for review first."
                value={form.autoSend}
                onChange={(autoSend) => setForm({ ...form, autoSend })}
              />
              <ToggleRow
                label="Dropship mode"
                description="When on, the customer's shipping address is used as the Ship-to on POs instead of your warehouse address."
                value={form.dropshipMode}
                onChange={(dropshipMode) => setForm({ ...form, dropshipMode })}
              />
            </div>

            {/* Save button */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Add Supplier'}
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="px-4 py-2.5 rounded-xl border border-border text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Delete Confirmation ───────────────────────────────────────────────── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This supplier will be removed. Any existing purchase orders linked to them will be
              retained but marked with a deleted-supplier note.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-xs text-amber-400 my-2">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>This action cannot be undone.</span>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Remove Supplier'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
